import { useWindowResize, getTextWidth } from "helpers/utility/utilities";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import XIcon from "assets/icons/XIcon";
import { FilterOption } from "types";
import classNames from "classnames";

import styles from "./styles.module.scss";

interface InputTabProps {
  text: string;
  className?: string;
  onRemove: () => void;
}

const InputTab = ({ text, className, onRemove }: InputTabProps) => {
  return (
    <div className={styles.tab} onClick={(e) => e.stopPropagation()}>
      <div
        data-testid="input-tab-remove-btn"
        className={styles.removeTabIcon}
        onClick={onRemove}
      >
        <XIcon />
      </div>
      <span className={className} title={text} data-testid="input-tab-text">
        {text}
      </span>
    </div>
  );
};

export interface InputLabelsProps {
  tabs: FilterOption[];
  onRemove: (id: number) => void;
}

const InputTabs = ({ tabs, onRemove }: InputLabelsProps) => {
  const [showExtraTabs, setShowExtraTabs] = useState(false);
  const [overflowCount, setOverflowCount] = useState(0);

  const extraTabsDropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overflowCountRef = useRef<HTMLSpanElement>(null);
  const width = useWindowResize();

  const visibleTabs = tabs.slice(0, tabs.length - overflowCount);
  const extraTabs = tabs.slice(-overflowCount);
  const showSingleTab = visibleTabs.length === 1;

  const tabRowClassnames = classNames(styles.tabRow, {
    [styles.tabRowWithClickableSpace]: overflowCount <= 0,
  });

  const tabClassnames = classNames(styles.tabText, {
    [styles.hideOverflow]: showSingleTab,
  });

  const handleOutsideClick = (e: any) => {
    if (
      overflowCountRef.current &&
      !overflowCountRef.current.contains(e.target)
    ) {
      setShowExtraTabs(false);
    }
  };

  useLayoutEffect(() => {
    if (!tabs.length) {
      setOverflowCount(0);
    }
    const container = containerRef.current;
    const tabsCount = tabs.length;
    const CLICKABLE_SPACE_WHEN_OVERFLOW = 15;
    const TAB_PADDING = 4 + 8;
    const CROSS_ICON = 16 + 4; // icon + gap
    const TAB_GAP = 8;
    let totalWidth = 0;

    if (container) {
      container.style.visibility = "hidden";
      let availableWidth = container.offsetWidth;
      let overflowCount = tabsCount;
      if (tabsCount > 0) {
        overflowCount = tabsCount;

        for (let i = 0; i < tabsCount; i++) {
          const labelWidth =
            getTextWidth(tabs[i].text, "14px") + TAB_PADDING + CROSS_ICON;
          const overflowTextWidth =
            getTextWidth(`+${overflowCount}`, "14px") + TAB_PADDING;

          const isSpaceAvailable =
            totalWidth +
              labelWidth +
              TAB_GAP +
              overflowTextWidth +
              CLICKABLE_SPACE_WHEN_OVERFLOW <=
            availableWidth;

          if (isSpaceAvailable) {
            totalWidth += labelWidth + TAB_GAP; // Increment total width with the gap
            overflowCount--;
          } else {
            if (tabsCount === 1) {
              overflowCount = 0;
            }
            break; // Exit the loop after determining overflow count
          }
        }

        if (tabsCount === overflowCount) {
          overflowCount--;
        }

        setOverflowCount(overflowCount);
        container.style.visibility = "visible";
      }
    }
  }, [tabs, width]);

  useEffect(() => {
    if (showExtraTabs) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showExtraTabs]);

  return (
    <div
      ref={containerRef}
      className={tabRowClassnames}
      onClick={handleOutsideClick}
    >
      {visibleTabs.map((tab, index) => {
        return (
          <InputTab
            key={index}
            text={tab.text}
            className={tabClassnames}
            onRemove={() => onRemove(tab.id)}
          />
        );
      })}
      {overflowCount > 0 && (
        <span
          ref={overflowCountRef}
          className={styles.extraCount}
          onMouseOver={() => setShowExtraTabs(true)}
          onMouseLeave={() => setShowExtraTabs(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <span data-testid="overflow-count-text">+{overflowCount}</span>
          {showExtraTabs && (
            <div className={styles.dropdownWrapper} ref={extraTabsDropdownRef}>
              <div className={styles.dropdownTip}></div>
              <div className={styles.dropdown}>
                <div className={styles.dropdownContent}>
                  {extraTabs.map((tab, index) => {
                    return (
                      <InputTab
                        key={index}
                        text={tab.text}
                        className={tabClassnames}
                        onRemove={() => onRemove(tab.id)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </span>
      )}
    </div>
  );
};

export default InputTabs;
